import { useCallback, useEffect, useState } from "react";
import { getAccount } from "../api/account";
import { buildDevHostAccount, isDevHostEnabled } from "./devHostAccount";
import type { Account } from "../types";

interface UseAccount {
  account: Account | null;
  loading: boolean;
  error: Error | null;
  /** Re-fetch the account. Call this after any mutation that affects
   *  license usage (invite, remove, leave, role change) so the meter
   *  reflects the new `numberOfUsedLicenses` without a page reload. */
  refresh: () => void;
}

export function useAccount(accountId: string | null | undefined): UseAccount {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(accountId));
  const [error, setError] = useState<Error | null>(null);
  // Tick is the manual-refresh signal — mirrors useInvitations' pattern.
  // Bumping it re-runs the effect without touching `accountId`.
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!accountId) {
      setAccount(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getAccount(accountId, { signal: controller.signal });
        setAccount(res);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        // AHA2-21: under the dev host shim (Vite dev server / per-PR sandbox)
        // the account fetch 401s on an unauthenticated origin, which would
        // leave the LicenseUsageMeter and "Buy licences" button hidden. Fall
        // back to a mock account so the sandbox can visually review them. Inert
        // in staging/canary/prod (isDevHostEnabled() is false there); real data
        // always wins when the fetch succeeds — this only runs on failure.
        if (isDevHostEnabled()) {
          setAccount(buildDevHostAccount(accountId));
          setError(null);
        } else {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [accountId, tick]);

  return { account, loading, error, refresh };
}
